import get from "lodash.get";

import {
  EVALUATION_TYPES,
  STEP_STORAGE_KEYS as S,
  STEP_STORAGE_KEYS,
  VOLUME_SCENARIOS,
} from "../constants/storageConstants";
import { getDistrictForCounty } from "../constants/geographyConstants";
import {
  DIRECTIONS,
  NO_CURRENT_INTERSECTION,
  ORIENTATIONS,
} from "../constants/intxConstants";
import { sumArray, sumValues } from "../util/mathHelpers";
import {
  getCrashesPerYear,
  getProjectDataWithAddedSafetyInfo,
} from "../screens/lifeCycleCost/outputs/outputsSafetyHelpers";
import {
  ICE_FORM_XLSX_INTX_CAPACITY,
  getBenefitCostRows,
  getIntersectionsWithCosts,
} from "../screens/lifeCycleCost/outputs/outputsHelpers";
import { BENEFIT_COST_KEYS } from "../constants/costConstants";
import { getEvaluationType } from "../util/dataSelectors";
import { getVcTableRows } from "./vc/vcHelper";
import {
  BC_CELL_STATUS_TO_FOOTNOTE,
  getBcCellStatus,
} from "../screens/lifeCycleCost/outputs/outputTableValues";

const COMMON_CELLS = [
  {
    path: `${S.PROJECT_INFORMATION}.projectName`,
    cell: "B9",
  },
  {
    path: `${S.PROJECT_INFORMATION}.analystName`,
    cell: "B10",
  },
  {
    path: `${S.PROJECT_INFORMATION}.agencyOrganization`,
    cell: "E10",
  },
];

/**
 *
 * @param {number} designYear like 2023
 * @param {number} designYearCrashes could be fatal injury or total crashes
 * @param {number} openingYear like 2023
 * @param {number} openingYearCrashes could be fatal injury or total crashes
 * @returns total number of crashes (either fatal injury or total) that are predicted between openingYear and designYear
 */
const getCrashesForStudyPeriod = ({
  designYear,
  designYearCrashes,
  openingYear,
  openingYearCrashes,
}) => {
  const yearsOfCrashes = new Array(designYear - openingYear + 1)
    .fill(0)
    .map((_, index) => {
      return (
        openingYearCrashes *
        Math.pow(
          designYearCrashes / openingYearCrashes,
          (openingYear + index - openingYear) / (designYear - openingYear)
        )
      );
    });
  return sumArray(yearsOfCrashes);
};

/**
 * @param {string} [baseCaseId] - optional
 * @param {object} projectData
 * @returns array of objects corresponding to the number of worksheets in /public/PennDOT-ICE-Forms.xlsx
 * we want to map through each worksheet, filling in data from the web tool into the xlsx spreadsheet
 *
 * The xlsx file is not made to handle ramp terminal inputs so this mapper function only works for at grade intersections in the Life Cycle Cost part of the tool
 */
export const getProjectDataToXlsxMapper = ({
  baseCaseId,
  projectData: projectDataInitial,
}) => {
  const isCapX =
    getEvaluationType(projectDataInitial) === EVALUATION_TYPES.CAP_X;
  const projectData = isCapX
    ? projectDataInitial
    : getProjectDataWithAddedSafetyInfo({
        projectData: projectDataInitial,
      });

  const vcTableRows = isCapX ? getVcTableRows(projectData) : [];

  /**
   * there are only cells for 6 intersection alternatives in the ice form spreadsheet
   */
  const first6Intersections = projectData[
    STEP_STORAGE_KEYS.INTERSECTIONS
  ].slice(0, ICE_FORM_XLSX_INTX_CAPACITY);
  const first6IntersectionsWithCosts = isCapX
    ? first6Intersections
    : getIntersectionsWithCosts(projectData).slice(
        0,
        ICE_FORM_XLSX_INTX_CAPACITY
      );

  const mapWithGet = ({ value, path, cell }) => ({
    value: value ?? get(projectData, path),
    cell,
  });

  const { county, EWFacilityName, majorFacility, NSFacilityName } =
    projectData[S.PROJECT_INFORMATION];
  const isEwMajor = majorFacility === ORIENTATIONS.EAST_WEST;
  const { currentIntersection, designYear, openingYear } =
    projectData[STEP_STORAGE_KEYS.GLOBAL_INPUT_DATA] || {};
  const majorDirections = {
    approach1: isEwMajor ? DIRECTIONS.EASTBOUND : DIRECTIONS.NORTHBOUND,
    approach2: isEwMajor ? DIRECTIONS.WESTBOUND : DIRECTIONS.SOUTHBOUND,
  };
  const minorDirections = {
    approach1: isEwMajor ? DIRECTIONS.NORTHBOUND : DIRECTIONS.EASTBOUND,
    approach2: isEwMajor ? DIRECTIONS.SOUTHBOUND : DIRECTIONS.WESTBOUND,
  };

  const benefitCostRows =
    baseCaseId ||
    (currentIntersection && currentIntersection !== NO_CURRENT_INTERSECTION)
      ? getBenefitCostRows({
          baseIntxDetails:
            first6IntersectionsWithCosts.find(({ id }) => id === baseCaseId) ??
            first6IntersectionsWithCosts.find(
              ({ isCurrentIntersection }) => isCurrentIntersection
            ),
          intersectionsWithCosts: first6IntersectionsWithCosts,
        })
      : [];
  return [
    {
      worksheetName: "Stage 1",
      cells: [
        ...COMMON_CELLS,
        { path: `${S.PROJECT_INFORMATION}.county`, cell: "B13" },
        { path: `${S.PROJECT_INFORMATION}.municipality`, cell: "G13" },
        { value: getDistrictForCounty(county), cell: "B14" },
        {
          value: isEwMajor ? EWFacilityName : NSFacilityName,
          cell: "E24", // major facility name
        },
        {
          value: isEwMajor ? NSFacilityName : EWFacilityName,
          cell: "E36", // minor facility name
        },
        { path: `${S.PROJECT_INFORMATION}.municipality`, cell: "G13" },
        ...[
          { turn: "left", cell: "F29" },
          { turn: "through", cell: "H29" },
          { turn: "right", cell: "J29" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_AM_PEAK}.${majorDirections.approach1}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F30" },
          { turn: "through", cell: "H30" },
          { turn: "right", cell: "J30" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_PM_PEAK}.${majorDirections.approach1}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F32" },
          { turn: "through", cell: "H32" },
          { turn: "right", cell: "J32" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_AM_PEAK}.${majorDirections.approach2}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F33" },
          { turn: "through", cell: "H33" },
          { turn: "right", cell: "J33" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_PM_PEAK}.${majorDirections.approach2}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F41" },
          { turn: "through", cell: "H41" },
          { turn: "right", cell: "J41" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_AM_PEAK}.${minorDirections.approach1}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F42" },
          { turn: "through", cell: "H42" },
          { turn: "right", cell: "J42" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_PM_PEAK}.${minorDirections.approach1}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F44" },
          { turn: "through", cell: "H44" },
          { turn: "right", cell: "J44" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_AM_PEAK}.${minorDirections.approach2}.${turn}`,
          cell,
        })),
        ...[
          { turn: "left", cell: "F45" },
          { turn: "through", cell: "H45" },
          { turn: "right", cell: "J45" },
        ].map(({ turn, cell }) => ({
          path: `${S.TRAFFIC_VOLUMES}.${VOLUME_SCENARIOS.OPENING_YEAR_PM_PEAK}.${minorDirections.approach2}.${turn}`,
          cell,
        })),
      ].map(mapWithGet),
    },
    {
      worksheetName: "Stage 2",
      cells: [
        ...COMMON_CELLS,
        { value: "Weekday AM Peak", cell: "D20" },
        { value: "Weekday PM Peak", cell: "H20" },
        { value: "Weekday AM Peak", cell: "D30" },
        { value: "Weekday PM Peak", cell: "H30" },
        ...first6Intersections.reduce((acc, { uniqueName }, index) => {
          const uniqueNameCells = ["A12", "D12", "H12", "A13", "D13", "H13"];
          return [...acc, { value: uniqueName, cell: uniqueNameCells[index] }];
        }, []),
        ...(isCapX ? vcTableRows : []).reduce(
          (
            acc,
            {
              openingYearAmPeak,
              openingYearPmPeak,
              designYearAmPeak,
              designYearPmPeak,
            },
            index
          ) => {
            return [
              ...acc,
              { value: openingYearAmPeak, cell: `D${index + 22}` },
              { value: openingYearPmPeak, cell: `H${index + 22}` },
              { value: designYearAmPeak, cell: `D${index + 32}` },
              { value: designYearPmPeak, cell: `H${index + 32}` },
            ];
          },
          []
        ),
        ...(isCapX ? [] : first6Intersections).reduce(
          (acc, { delay }, index) => {
            return [
              ...acc,
              { value: delay.openingYear.amPeak, cell: `E${index + 22}` },
              { value: delay.openingYear.pmPeak, cell: `I${index + 22}` },
              { value: delay.designYear.amPeak, cell: `E${index + 32}` },
              { value: delay.designYear.pmPeak, cell: `I${index + 32}` },
            ];
          },
          []
        ),
        ...(isCapX ? [] : first6Intersections).reduce(
          (acc, { costParameters }, index) => {
            return costParameters
              ? [
                  ...acc,
                  {
                    value:
                      costParameters.constructionCost +
                      costParameters.designCost +
                      costParameters.rightOfWayCost,
                    cell: index < 3 ? `C${index + 44}` : `H${index - 3 + 44}`,
                  },
                  {
                    value: "PE, ROW, Construction, Contingency",
                    cell: index < 3 ? `D${index + 44}` : `I${index - 3 + 44}`,
                  },
                ]
              : acc;
          },
          []
        ),
        ...(isCapX ? [] : first6Intersections).reduce(
          (acc, intersection, index) => {
            const crashes = getCrashesPerYear({
              projectData,
              intersection,
            });

            const fatalInjuryCrashes = getCrashesForStudyPeriod({
              designYear,
              designYearCrashes: crashes.designYear.fatalInjury,
              openingYear,
              openingYearCrashes: crashes.openingYear.fatalInjury,
            });
            const totalCrashes = getCrashesForStudyPeriod({
              designYear,
              designYearCrashes: crashes.designYear.total,
              openingYear,
              openingYearCrashes: crashes.openingYear.total,
            });

            return [
              ...acc,
              { value: totalCrashes, cell: `H${index + 52}` },
              { value: fatalInjuryCrashes, cell: `I${index + 52}` },
            ];
          },
          []
        ),
        ...(isCapX ? [] : first6IntersectionsWithCosts).reduce(
          (acc, intersection, index) => {
            return [
              ...acc,
              {
                value: sumValues(intersection.output.total), // "Net present value" column in the spreadsheet
                cell: `C${index + 93}`,
              },
            ];
          },
          []
        ),
        ...(isCapX ? [] : benefitCostRows).reduce((acc, row, index) => {
          const cost = row[BENEFIT_COST_KEYS.VALUE_OF_COSTS];

          const bcRatioCellStatus = getBcCellStatus({
            bcValue: row[BENEFIT_COST_KEYS.BC_RATIO],
            cost,
          });
          const safetyBcRatioCellStatus = getBcCellStatus({
            bcValue: row[BENEFIT_COST_KEYS.SAFETY_BC_RATIO],
            cost,
          });
          return [
            ...acc,
            {
              value:
                BC_CELL_STATUS_TO_FOOTNOTE[bcRatioCellStatus] ??
                row[BENEFIT_COST_KEYS.BC_RATIO],
              cell: `E${index + 93}`,
            },
            {
              value:
                BC_CELL_STATUS_TO_FOOTNOTE[safetyBcRatioCellStatus] ??
                row[BENEFIT_COST_KEYS.SAFETY_BC_RATIO],
              cell: `J${index + 52}`,
            },
          ];
        }, []),
      ].map(mapWithGet),
    },
    {
      worksheetName: "Stage 3",
      cells: [...COMMON_CELLS].map(mapWithGet),
    },
  ];
};
